<template>
  <div>
    <player-breadcrumb
      class="mb-2"
      :view="$t('player_profile.events')"
      :player="player"
    >
      <v-row>
        <v-spacer />
        <v-col cols="auto">
          <v-btn
            color="primary"
            text
            class="text-none"
            @click="filtersDialog = true"
          >
            {{ $t('button.filters') }}
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <layout-selector
            :selected-layout.sync="selectedLayout"
            :layouts.sync="layouts"
            @add-layout="openAddLayoutDialog"
            @edit-layout="openEditLayoutDialog"
          />
        </v-col>
      </v-row>
    </player-breadcrumb>

    <grid-layout
      v-if="selectedLayout"
      :key="selectedLayout.name"
      :layout="selectedLayout.grid"
      :col-num="12"
      :row-height="70"
      :is-draggable="false"
      :is-resizable="false"
      :vertical-compact="true"
      :margin="[10, 10]"
      :use-css-transforms="true"
      @layout-ready="layoutReady = true"
    >
      <grid-item
        v-for="item in selectedLayout.grid"
        :key="item.i"
        :x="item.x"
        :y="item.y"
        :w="item.w"
        :h="item.h"
        :i="item.i"
      >
        <div
          v-if="layoutReady"
          class="fill-height"
        >
          <div
            v-if="item.type === 'table'"
            class="fill-height"
          >
            <v-card class="rounded-lg fill-height">
              <v-card-title>
                <v-row align="center">
                  <v-col>
                    Players
                  </v-col>
                  <v-col
                    cols="auto"
                  >
                    <variable-selector
                      v-if="variables.data"
                      :variables="variables.data"
                      :layout-variables.sync="tableHeaders"
                      @update="saveLayoutVariables"
                    />
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text class="pa-0">
                <v-data-table
                  :items="events.data"
                  :headers="layoutVariables"
                  :loading="events.loading || tableHeaders.loading"
                  hide-default-footer
                  disable-pagination
                  fixed-header
                  height="380"
                >
                  <template v-slot:item="{item}">
                    <tr
                      style="cursor: pointer"
                      @click="navigateTo(item)"
                    >
                      <td style="width: 200px">
                        <div style="max-width: 170px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis">
                          {{ item.title }}
                        </div>
                        <div class="text-caption text-grey">
                          {{ $dayjs(item.start_at).format('ddd, ll HH:mm:ss') }}
                        </div>
                      </td>
                      <td
                        v-for="tableHeader in layoutVariables.slice(1)"
                        :key="tableHeader.id"
                      >
                        <table-bar-value
                          :value="item.player_stats ? item.player_stats[tableHeader.value] : null"
                          :max="events.maxValues[tableHeader.value] || 100"
                          :color="getColorForValue(item.player_stats ? item.player_stats[tableHeader.value] : null, events.maxValues[tableHeader.value])"
                        >
                          {{ item.player_stats ? item.player_stats[tableHeader.value] : '-' }}
                        </table-bar-value>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </div>
          <div
            v-else-if="item.type === 'card'"
            class="fill-height"
          >
            <stat-card
              :loading="aggregationsStats.loading"
              :title="getVariableName(item.key)"
              :value="aggregationsStats.data.group ? aggregationsStats.data.group[item.key] : null"
              :max="15000"
              :color="getColorFromValue(aggregationsStats.data.group ? aggregationsStats.data.group[item.key] : null, 15000)"
            />
          </div>
          <div
            v-else-if="item.type === 'chart'"
            class="fill-height"
          >
            <performance-graph-individual
              v-if="aggregationsStats.data.group"
              class="fill-height"
              :chart="item"
              :stats="aggregationsStats.data.group"
              :variables="variables.data"
              :height="String(item.h * 70 - 50)"
            />
          </div>
          <div
            v-else-if="item.type === 'line_chart'"
            class="fill-height"
          >
            <performance-trend-chart
              :variable-key="item.key"
              :events="events.data"
              :title="getVariableName(item.key)"
            />
          </div>
        </div>
      </grid-item>
    </grid-layout>

    <advanced-performance-filter-dialog
      :visible.sync="filtersDialog"
      :filters="advancedFilters"
      :events="events"
      @handle:advanced="fetchPlayerEvents(); fetchAdvancedPerformanceStats()"
    />
  </div>
</template>

<script>
import { EventApiService, LayoutApiService, PlayerApiService } from '@/data/services'
import { getColorForValue, processMaxValues } from '@/utils/helpers'
import PlayerBreadcrumb from '@/components/players/PlayerBreadcrumb.vue'
import { VariableApiService } from '@/data/services/VariableApiService'
import { cloneDeep, orderBy } from 'lodash'
import TableBarValue from '@/components/helpers/TableBarValue.vue'
import VariableSelector from '@/components/performance/VariableSelector.vue'
import { RouteNames } from '@/router/routes'
import { DashboardType } from '@/data/models/Dashboard'
import { GridItem, GridLayout } from 'vue-grid-layout'
import { EventBus } from '@/plugins/bus'
import AdvancedPerformanceFilterDialog from '@/components/dialogs/AdvancedPerformanceFilterDialog.vue'
import PerformanceTrendChart from '@/components/charts/implementation/PerformanceTrendChart.vue'
import PerformanceGraphIndividual from '@/components/performance/PerformanceGraphIndividual.vue'
import StatCard from '@/components/StatCard.vue'
import LayoutSelector from '@/components/LayoutSelector.vue'
import debounce from 'lodash/debounce'
import { dashboardConfig } from '@/data/models/DashboardConfig'

export default {
  name: 'PlayerEventsView',
  components: {
    LayoutSelector,
    StatCard,
    PerformanceGraphIndividual,
    PerformanceTrendChart,
    AdvancedPerformanceFilterDialog,
    VariableSelector,
    TableBarValue,
    PlayerBreadcrumb,
    GridLayout,
    GridItem
},
  data: () => ({
    player: {
      data: {},
      loading: false
    },
    events: {
      data: [],
      maxValues: {},
      loading: false
    },
    aggregationsStats: {
      data: {},
      loading: false
    },
    variables: {
      data: [],
      loading: false
    },
    advancedFilters: {
      dateRange: [],
      eventIds: [],
      teamId: 'all',
      eventType: null
    },
    filtersDialog: false,
    tableHeadersGPS: [],
    selectedLayoutObject: null,
    selectedVariablesObject: null,
    layoutReady: false
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasLayoutManagement() {
      return this.user && this.user.organisation_settings?.has_layout_management
    },
    layouts() {
      return this.$store.getters[this.$storeGetters.APP_LAYOUTS]
        .filter(layout => layout.dashboard === DashboardType.PLAYER_EVENTS)
    },
    layoutVariables() {
      return this.selectedLayout ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.tableHeaders.map(header => {
        return {
          ...header,
          text: this.$t(`variables.${header.value}`)
        }
      }), ['order'])) : []
    },
    selectedLayout: {
      get() {
        if (this.selectedLayoutObject) {
          return cloneDeep(this.selectedLayoutObject)
        }
        return cloneDeep(this.layouts[0])
      },
      set(value) {
        if (value !== this.selectedLayoutObject) this.layoutReady = false
        this.selectedLayoutObject = value
      }
    },
    tableHeaders: {
      get() {
        if (this.selectedVariablesObject) {
          return this.selectedVariablesObject
        } else if (this.selectedLayout) {
          return this.selectedLayout.variables
        }
        return []
      },
      set(value) {
        this.selectedVariablesObject = value
      }
    }
  },
  created() {
    if (this.$route.query.dateRange) {
      this.advancedFilters.dateRange = this.$route.query.dateRange
      this.advancedFilters.eventIds = this.$route.query.eventIds
      this.advancedFilters.eventType = this.$route.query.eventType
      this.$router.replace({ query: {} })
    } else {
      this.advancedFilters.dateRange = [this.$dayjs().subtract(1, 'month').format('YYYY-MM-DD'), this.$dayjs().format('YYYY-MM-DD')]
    }

    EventBus.$on(this.$events.LAYOUT_UPDATED, () => {
      this.selectedLayoutObject = null
    })

    EventBus.$on(this.$events.LAYOUT_DELETED, (layout) => {
      this.selectedLayoutObject = this.layouts.filter(l => l.id !== layout.id)[0]
    })

    EventBus.$on(this.$events.LAYOUT_UPDATED, () => {
      this.selectedLayoutObject = null
    })

    this.fetchPlayerDetails()
    this.fetchPlayerEvents()
    this.fetchAdvancedPerformanceStats()
    this.fetchVariables()
  },
  methods: {
    getColorForValue,
    getColorFromValue(value, max) {
      return getColorForValue(value, max)
    },
    async fetchPlayerDetails() {
      try {
        this.player.loading = true

        const resp = await PlayerApiService.getPlayerDetails(this.$route.params.id)
        this.player.data = resp.data
      } catch (e) {
        // todo
      } finally {
        this.player.loading = false
      }
    },
    async fetchPlayerEvents() {
      try {
        this.events.loading = true

        const queryParams = {
          page: 1,
          per_page: 100,
          types: this.advancedFilters.eventType ? [this.advancedFilters.eventType] : undefined,
          from: this.advancedFilters.dateRange[0] ?? undefined,
          to: this.advancedFilters.dateRange[1] ?? undefined,
          has_integration: true,
          order: 'desc',
          ids: this.advancedFilters.eventIds
        }

        const resp = await PlayerApiService.getEvents(this.$route.params.id, queryParams)

        this.events.data = resp.data
        this.events.pagination = resp.meta

        this.events.maxValues = processMaxValues(resp.data.map(event => event.player_stats || {}), 1.1)
      } catch (e) {
        // todo
      } finally {
        this.events.loading = false
      }
    },
    async fetchAdvancedPerformanceStats() {
      if (this.aggregationsStats.loading) return

      try {
        this.aggregationsStats.loading = true

        const params = {
          player_ids: [this.$route.params.id],
          event_types: this.advancedFilters.eventType ? [this.advancedFilters.eventType] : undefined,
          event_ids: this.advancedFilters.eventIds,
          date_from: this.advancedFilters.dateRange[0] ?? undefined,
          date_to: this.advancedFilters.dateRange[1] ?? undefined
        }

        const result = await EventApiService.getAdvancedPerformanceStats(params)

        this.aggregationsStats.data = result

        this.aggregationsStats.maxValues = processMaxValues(result.players.map(player => player.player_stats || {}))
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_advanced_statistics'),
          color: 'error'
        })

        throw e
      } finally {
        this.aggregationsStats.loading = false
      }
    },
    async fetchVariables() {
      this.variables.loading = true

      try {
        const result = await VariableApiService.getVariables({
          category: dashboardConfig[DashboardType.PLAYER_EVENTS].variable_categories
        })

        this.variables.data = result.data.map(v => {
          return {
            ...v,
            text: v.suffix ? this.$t(`variables.${v.value}`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}`),
            long_text: v.suffix ? this.$t(`variables.${v.value}.long`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}.long`)
          }
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.variables.loading = false
      }
    },
    saveLayoutVariables: debounce(async function () {
      if (!this.hasLayoutManagement) return

      this.submitting = true

      try {
        const data = {
          variables: this.tableHeaders.map(v => ({
            id: v.id,
            order: v.order,
            aggregation: v.aggregation
          }))
        }

        await LayoutApiService.saveVariables(this.selectedLayout.id, data)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.variables_save.error'),
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    }, 1000),
    navigateTo(item) {
      this.$router.push({ name: RouteNames.PERFORMANCE_EVENT_PLAYER, params: { eventId: item.id, playerId: this.player.data.id } })
    },
    openAddLayoutDialog() {
      EventBus.$emit(this.$events.ADD_LAYOUT, {
        dashboard: DashboardType.PLAYER_EVENTS
      })
    },
    openEditLayoutDialog() {
      EventBus.$emit(this.$events.EDIT_LAYOUT, {
        dashboard: DashboardType.PLAYER_EVENTS,
        selectedLayout: this.selectedLayout
      })
    },
    getVariableName(value) {
      return this.variables.data.find(variable => variable.value === value)?.long_text
    }
  }
}
</script>

<style>

</style>
